import React from 'react';
import { ShieldCheck, Layers, Wrench, AlertTriangle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';

const ASSURANCES = [
  { icon: ShieldCheck, name: 'Contractual assurance',
    body: 'Know what the contract requires and whether the obligation has been satisfied.' },
  { icon: Layers, name: 'Asset assurance',
    body: 'Connect assets, condition, intervention and long-term risk.' },
  { icon: Wrench, name: 'SFG20 assurance',
    body: 'Bring maintenance requirements, completion and supporting evidence into the assurance environment.' },
  { icon: AlertTriangle, name: 'Management assurance',
    body: 'Surface exceptions and emerging risks before they become larger problems.' },
];

const V2VideraVanguard = () => {
  useSEO({
    title: 'Videra Vanguard | PFI, P3 & FM operational assurance',
    description: 'Contract assurance. Asset assurance. One line of sight. Videra Vanguard connects contractual obligations, SFG20, evidence, exceptions and operational risk.',
    path: '/videra/vanguard',
  });

  return (
    <div data-testid="v2-videra-vanguard">
      <ProductHero
        eyebrow="Videra Vanguard"
        title="Protect the contract. Assure the operation."
        subtitle="Contract assurance. Asset assurance. One line of sight."
        body={(
          <>
            <p>Long-term PFI, P3 and FM environments contain large numbers of contractual requirements, assets, maintenance obligations and evidence requirements.</p>
            <p>Videra Vanguard creates a structured first line of defence — translate requirements into visible obligations, assign ownership, track what is due, capture supporting evidence, identify exceptions and understand emerging contractual and asset risk.</p>
          </>
        )}
        primary={{ label: 'Explore Videra Vanguard', href: 'https://demo.videra.app/explore' }}
        secondary={{ label: 'Discuss Vanguard with Keystone', to: '/contact' }}
        heroImage="/images/healthcare/hc-handback-readiness.jpg"
        heroImageAlt="Videra Vanguard contractual assurance"
        testid="vanguard-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Four lines of assurance</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">From obligation to evidence — visible.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {ASSURANCES.map((a) => (
              <div key={a.name} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`vanguard-line-${a.name.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center mb-3">
                  <a.icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-lg font-medium text-slate-900 mb-2">{a.name}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-4">Vanguard + Hard Services</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight max-w-3xl">Assure the requirement. Control the work.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-slate-800 rounded-xl p-6 bg-slate-900/40">
              <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-3">Vanguard</p>
              <p className="text-slate-200 mb-3">What must happen — and can we prove it?</p>
              <ul className="text-sm text-slate-400 space-y-1">
                {['Contractual obligations','Asset requirements','SFG20','Assurance','Evidence','Exceptions','Risk'].map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
            <div className="border border-slate-800 rounded-xl p-6 bg-slate-900/40">
              <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-3">Hard Services</p>
              <p className="text-slate-200 mb-3">What work must we deliver — and where is it?</p>
              <ul className="text-sm text-slate-400 space-y-1">
                {['Lifecycle interventions','Projects','Approvals','Delivery','Closeout','Evidence','Handback gates'].map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          </div>
          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400 font-semibold mb-4">Together</p>
            <Workflow variant="dark" testid="vanguard-together-workflow" steps={['Contract','Obligation','Asset','Lifecycle requirement','Project','Delivery','Evidence','Assurance','Handback']} />
          </div>
          <p className="mt-10 text-slate-400 text-sm max-w-3xl">
            Customers can use either platform independently or combine them where connected operational and contractual assurance is required.
          </p>
        </div>
      </section>
    </div>
  );
};

export default V2VideraVanguard;
